import {IPresenter} from "@/rawdogjs/src/interfaces/IPresenter";
import {ErrorBase} from "@/rawdogjs/src/core/ErrorBase";
import {SignUpView} from "./signUpView";
import {TenantApi} from "../../services/api/base/TenantApi";
import {UserStore} from "../../data/stores/UserStore";
import {Router} from "@/rawdogjs/src/router";
import {HandleApi} from "../../services/api/base/HandleApi";
import {ParcelDependencyContainer} from "@/rawdogjs/src/core/ParcelDependencyContainer";
import {ValidationError} from "@/rawdogjs/src/core/ValidationError";
import {UuidHelper} from "../../shared/IdentityHelper";
import DebounceHelper from "../../shared/DebouceHelper";
import {AuthenticationApi} from "../../services/api/base/AuthenticationApi";
import {CookieManager} from "./CookieManager";
import {LoadingPresenter} from "../loading/loadingPresenter";
import {RenderComponent} from "@/rawdogjs/src/renderRoute";

export class SignUpPresenter implements IPresenter {

    public errors: ErrorBase[] = [];
    public serverErrors: ErrorBase[] = [];

    private PASSWORD_MAX_LENGTH = 8;

    public readonly _view: SignUpView;

    private readonly _tenantApi: TenantApi;
    private readonly _userStore: UserStore;
    private readonly _router: Router;
    private _handleApi: HandleApi;
    private _authenticationApi: AuthenticationApi;
    private _cookieManager: CookieManager;
    private _loading: LoadingPresenter;

    constructor(view: SignUpView, router: Router, dependencyInjection: ParcelDependencyContainer) {

        this._view = view;
        this._router = router;

        this._tenantApi = dependencyInjection.resolve<TenantApi>('TenantApi');
        this._handleApi = dependencyInjection.resolve<HandleApi>('HandleApi');
        this._userStore = dependencyInjection.resolve<UserStore>('UserStore');
        this._authenticationApi = dependencyInjection.resolve<AuthenticationApi>('BearerTokenApi');
        this._cookieManager = dependencyInjection.resolve<CookieManager>('CookieManager');

    }

    async handleFormChange(fieldSelector: string, value: string, fieldName: string) {
        // validate and then set the field errors
        await this.validate();
        this._view.setFieldErrors(this.getCombinedErrors(), fieldName);
    }

    async initialize() {

        const renderComponent = new RenderComponent(this._router);
        this._loading = await renderComponent.render<LoadingPresenter>('loading');
        this._view.$element.append(this._loading._view.$element);
        
        // did this to prevent the errors around multiple sign-ups in a row due to cookie mix-ups 
        try {
            this._loading.setBody('Loading...');
            this._loading.show(true);
            
            const authResponse = await this._authenticationApi.post(
                {
                    logout: {}
                },
                null,
                false,
                true
            );
            
        }
        catch(logoutError){
            console.log(logoutError);
        }
        finally{
            await this.load();
            this._loading.show(false);
        }

    }
    
    async load(){
        const params = this._router.getQueryParams();

        this._view.email = params['email'] ?? '';
        this._view.firstName = params['firstName'] ?? '';
        this._view.lastName = params['lastName'] ?? '';
        this._view.publicKey = params['publicKey'] ?? '';
        this._view.publicKeyId = params['publicKeyId'] ?? '';
        this._view.challenge = params['signedChallenge'] ?? '';

        // disable input if value is passed via query
        if (this._view.publicKey)
            this._view.isPublicKeyInputEnabled = false;

        if (this._view.challenge)
            this._view.isChallengeInputEnabled = false;

        this._view.addOnClickHandler('#btn-sign-up', async () => {
            await this.signUp();
        })

        const emailDebouncedFunction
            = this.createEmailDebouncedFunction();

        this._view.addFieldEventHandler('email', ['input', 'blur'],
            async (fieldSelector, value, fieldName) => {
                await this.handleFormChange(fieldSelector, value, fieldName);
                emailDebouncedFunction.debouncedFunction.bind(this)(fieldSelector, value, fieldName);
            }
        );

        const handleDebouncedFunction = this.createHandleDebouncedFunction();

        this._view.addFieldEventHandler('handle', ['input', 'blur'],
            async (fieldSelector, value, fieldName) => {

                await this.handleFormChange(fieldSelector, value, fieldName)

                const clientErrors = this.errors.filter(x => x.fieldName === 'handle');

                if (clientErrors && clientErrors.length > 0) {
                    this._view.isExistingHandleLoading = false;
                    this._view.isHandleAvailable = false;
                    // cancel calls
                    handleDebouncedFunction.abort();
                } else {
                    // queue next call
                    handleDebouncedFunction.debouncedFunction.bind(this)(fieldSelector, value, fieldName);
                }

            });

        this._view.addFieldEventHandler('firstName', ['input', 'blur'],
            async (fieldSelector, value, fieldName) => await this.handleFormChange(fieldSelector, value, fieldName));

        this._view.addFieldEventHandler('lastName', ['input', 'blur'],
            async (fieldSelector, value, fieldName) => await this.handleFormChange(fieldSelector, value, fieldName));

        this._view.addFieldEventHandler('password', ['input', 'blur'],
            async (fieldSelector, value, fieldName) => await this.handleFormChange(fieldSelector, value, fieldName));

        // start up with the sign-up button being disabled
        this._view.isSignUpButtonEnable = false;

        this._view.scrollToTop();

        // set focus on email field on start up
        setTimeout(() => {
            this._view.focusOnElement('email');
        }, 500);

        this._loading.show(false);
    }

    isEmailValid(email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return email && emailPattern.test(email);
    }

    async validate() {

        // clear
        this.errors = [];

        if (!this._view.handle) {
            this.errors.push(
                new ValidationError(
                    // generate these instead?
                    UuidHelper.newId(),
                    'Handle cannot be empty.',
                    'signUpPresenter.HandleCannotBeEmpty',
                    'ec6eacd3-d969-4c24-8f8c-0638a94c09e8',
                    'handle'
                )
            );
        } else if (this._view.handle.length < 4 || this._view.handle.length > 20) {
            this.errors.push(
                new ValidationError(
                    UuidHelper.newId(),
                    'Handles must be at least 4 characters and not more than 20 characters.',
                    'signUpPresenter.HandleHasInvalidLength',
                    '32ea147d-a887-420c-b855-554fac108a5e',
                    'handle'
                )
            );
        } else {

            const handlePattern = /^[a-zA-Z0-9_]+$/;

            if (!handlePattern.test(this._view.handle)) {
                this.errors.push(
                    new ValidationError(
                        UuidHelper.newId(),
                        'Handles can only contain capital letters, lower case letters and underscore.',
                        'signUpPresenter.HandleHasInvalidCharacters',
                        '0ad9a5fd-90ea-4d87-a758-0642933ab72c',
                        'handle'
                    )
                );
            }
        }

        if (!this._view.email) {
            this.errors.push(
                new ValidationError(
                    UuidHelper.newId(),
                    'Email address cannot be empty.',
                    'signUpPresenter.EmailCannotBeEmpty',
                    '0935dc2e-b521-4ce1-8455-3ee85a167e82',
                    'email'
                )
            );
        } else {
            if (!this.isEmailValid(this._view.email)) {
                this.errors.push(
                    new ValidationError(
                        UuidHelper.newId(),
                        'Invalid email address.',
                        'signUpPresenter.InvalidEmailAddress',
                        '0935dc2e-b521-4ce1-8455-3ee85a167e82',
                        'email'
                    )
                );
            }
        }

        if (!this._view.firstName) {
            this.errors.push(
                new ValidationError(
                    UuidHelper.newId(),
                    'First name cannot be empty.',
                    'signUpPresenter.FirstNameCannotBeEmpty',
                    '101aa203-d688-457a-874c-0ec4216360dd',
                    'firstName'
                )
            );
        }

        if (!this._view.lastName) {
            this.errors.push(
                new ValidationError(
                    'abdf225a-2a0a-4101-90e9-011a4408cbc0',
                    'Last name cannot be empty.',
                    'signUpPresenter.LastNameCannotBeEmpty',
                    '9b6ed329-4db6-4c9d-90cd-d617d46ee94a',
                    'lastName'
                )
            );
        }

        if (!this._view.password || this._view.password.length < this.PASSWORD_MAX_LENGTH) {
            this.errors.push(
                new ValidationError(
                    UuidHelper.newId(),
                    `Password cannot be less than ${this.PASSWORD_MAX_LENGTH} characters.`,
                    'signUpPresenter.PasswordCannotBeEmpty',
                    '9b6ed329-4db6-4c9d-90cd-d617d46ee94a',
                    'password'
                )
            );
        }

        if (!this._view.challenge) {
            this.errors.push(
                new ValidationError(
                    UuidHelper.newId(),
                    'Invalid challenge token.',
                    'signUpPresenter.InvalidChallengeToken',
                    '724b8a5e-74d1-455b-8fac-2d39ce175ed4',
                    'challenge'
                )
            );
        }

        this._view.isSignUpButtonEnable = (this.getCombinedErrors().length === 0);


    }

    getCombinedErrors() {
        return this.errors.concat(this.serverErrors);
    }

    async getEmail(email: string) {

        return await this._tenantApi.tryGet(
            null,
            null,
            {
                "email": email
            },
            false
        );

    }

    async getHandle(handle: string) {

        return await this._handleApi.tryGet(
            null,
            null,
            {
                "search": handle
            },
            false
        );

    }

    async signUp() {

        try {

            const signUpResult = await this._tenantApi.post(
                {
                    "signUp": {
                        email: this._view.email,
                        firstName: this._view.firstName,
                        lastName: this._view.lastName,
                        password: this._view.password,
                        signedChallenge: this._view.challenge,
                        publicKey: this._view.publicKey,
                        publicKeyId: this._view.publicKeyId,
                        handle: this._view.handle
                    }
                },
                null,
                false,
                true
            );

            if (signUpResult?.data?.bearerToken) {

                // await this._userStore.fetch(this._tenantApi);

                // const getHandleResponse = await this.getHandle(this._view.handle);
                // const existingHandle = getHandleResponse?.data;
                //
                // if (existingHandle != null && existingHandle.tenantId !== this._userStore.getTenantId())
                //     alert(`The handle "${this._view.handle}" was taken while you were registering.`);

                // // handle
                // const handlePutResult = await this._handleApi.put({
                //         handle: this._view.handle,
                //         versionStamp: !existingHandle ? null : existingHandle.concurrencyVersion,
                //         tenantId: this._userStore.getTenantId()
                //     },
                //     this._userStore.getLandlordTenantId(),
                //     true
                //);

                await this._userStore.fetch(this._tenantApi);

                //console.log('handle result: ' + handlePutResult);

                this._router.navigate('/#/home');
            }

        } catch (e) {

            if (e.status === 400) {
                if (e.response?.data?.errors && e.response?.data?.errors.length > 0) {
                    this._view.setErrorList(e.response?.data?.errors);
                }
            }

        }


    }


    public createEmailDebouncedFunction() {
        return DebounceHelper.createDebounceFunction<[string, string, string]>(
            async (fieldSelector, value, fieldName: string, signal) => {

                this.serverErrors = this.serverErrors.filter(x => x.fieldName !== 'email');

                //this._view.isExistingHandleLoading = true;

                try {
                    const result = await this.getEmail(this._view[fieldName]);

                    if (signal.aborted) return;

                    if (result !== null) {
                        this.serverErrors.push(
                            new ValidationError(
                                UuidHelper.newId(),
                                `Email is already registered, please log-in instead.`,
                                'signUpPresenter.EmailAlreadyExists',
                                '4947902b-ba18-4b84-aecf-96abbf6bd6fc',
                                'email'
                            )
                        );
                    }

                    await this.handleFormChange(fieldSelector, value, fieldName)

                } catch (e) {
                    if (signal.aborted) return;
                    alert('Server encountered an error.');
                    throw e;
                } finally {
                    if (!signal.aborted) {
                        // this._view.isExistingHandleLoading = false;
                    }
                }
            },
            1000
        );
    }

    public createHandleDebouncedFunction() {
        return DebounceHelper.createDebounceFunction<[string, string, string]>(
            async (fieldSelector, value, fieldName: string, signal) => {

                this._view.isExistingHandleLoading = true;

                // reset errors
                this.serverErrors = this.serverErrors.filter(x => x.fieldName !== 'handle');

                try {
                    const result = await this.getHandle(this._view[fieldName]);

                    if (signal.aborted) return;

                    if (result !== null) {
                        this.serverErrors.push(
                            new ValidationError(
                                UuidHelper.newId(),
                                `Handle "${this._view[fieldName]}" is not available, please try a different handle.`,
                                'signUpPresenter.HandleIsNotAvailable',
                                '20c87cb9-e9ed-4e1e-b357-47bac3ea73b3',
                                'handle'
                            )
                        );

                    }

                    await this.handleFormChange(fieldSelector, value, fieldName);

                } catch (e) {
                    if (signal.aborted) return;
                    alert('Server encountered an error.');
                    throw e;
                } finally {
                    if (!signal.aborted) {
                        this._view.isExistingHandleLoading = false;
                    }
                }
            },
            1000
        );
    }

    inject() {
    }

}
